/**
 * ダッシュボード Legacy SVG チャート共通: クリップボード / 画像エクスポート
 */

import { useCallback, type RefObject } from 'react';
import { copySvgElementAsPngToClipboard, normalizeSvgDimensionsForExport } from '../../../../utils/exportUtils';

export function useKeisukeDashboardChartSvgExport(
  chartContainerRef: RefObject<HTMLDivElement | null>,
  baseName: string
) {
  const getExportBaseName = useCallback(
    () => `${baseName}_${new Date().toISOString().slice(0, 10)}`,
    [baseName]
  );

  const renderSvgToCanvas = useCallback((callback: (canvas: HTMLCanvasElement) => void) => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    normalizeSvgDimensionsForExport(clone);
    const svgString = new XMLSerializer().serializeToString(clone);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      callback(canvas);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [chartContainerRef]);

  const handleExportSvg = useCallback(() => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    normalizeSvgDimensionsForExport(clone);
    const svgString = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${getExportBaseName()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [chartContainerRef, getExportBaseName]);

  const handleCopySvgToClipboard = useCallback(async () => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    await copySvgElementAsPngToClipboard(svg);
  }, [chartContainerRef]);

  const handleExportPng = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${getExportBaseName()}.png`;
      a.click();
    });
  }, [renderSvgToCanvas, getExportBaseName]);

  const handleExportJpeg = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/jpeg', 0.92);
      a.download = `${getExportBaseName()}.jpg`;
      a.click();
    });
  }, [renderSvgToCanvas, getExportBaseName]);

  const handleExportTiff = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${getExportBaseName()}.tiff`;
      a.click();
    });
  }, [renderSvgToCanvas, getExportBaseName]);

  return {
    handleCopySvgToClipboard,
    handleExportPng,
    handleExportSvg,
    handleExportTiff,
    handleExportJpeg,
  };
}
